import { PagoMiembroFields, pagoMiembroValido, pagoMiembroPayload } from "../eventos/PagoMiembroFields";
import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { getConceptosContabilidadActivos, getTiposTickets, postAsistentes } from "../../services/apis";
import { getOpcionesEncargadosEvento, getPreciosTicketsMap } from "../eventos/eventosUtils";

const invitadosVacios = {
    invitados: 0,
    invitados_nino1: 0,
    invitados_nino2: 0,
};

const getOptionId = (option) => Number(option?.id_usuario ?? option?.id ?? option?.value ?? 0);

const getInvitadosValor = (invitados, clave) => {
    if (!invitados) {
        return 0;
    }

    const claves = {
        invitados: ["invitados"],
        invitados_nino1: ["invitados_nino1", "invitados_niño1", "invitados_niÃ±o1", "invitados_niÃƒÂ±o1"],
        invitados_nino2: ["invitados_nino2", "invitados_niño2", "invitados_niÃ±o2", "invitados_niÃƒÂ±o2"],
    };

    return claves[clave].reduce((valor, key) => valor || Number(invitados[key] || 0), 0);
};

const normalizarInvitados = (invitados) => ({
    invitados: getInvitadosValor(invitados, "invitados"),
    invitados_nino1: getInvitadosValor(invitados, "invitados_nino1"),
    invitados_nino2: getInvitadosValor(invitados, "invitados_nino2"),
});

const normalizarAsistente = (asistente) => ({
    ...asistente,
    miembro_asiste: Number(asistente.miembro_asiste ?? 1),
    id_usuario: Number(asistente.id_usuario),
    nombre: asistente.nombre || asistente.label || "Sense nom",
    invitados: normalizarInvitados(asistente.invitados),
});

const getInitialEventoForm = (tarea) => ({
    miembro_paga: Number(tarea?.miembro_paga || 0),
    precio_miembro: tarea?.precio_miembro ?? "",
    asunto: tarea?.asunto || "",
    descripcion: tarea?.descripcion || "",
    fecha: tarea?.fecha || "",
    hora_inicio: tarea?.hora_inicio || "",
    hora_final: tarea?.hora_final || "",
    invitados_penya: Math.max(0, Number(tarea?.invitados_penya || 0)),
    precios_tickets: getPreciosTicketsMap(tarea),
    id_apartado: tarea?.id_apartado || "",
});

export const EventoComidaDialog = ({
    open,
    setOpen,
    tarea,
    opTrabajadores,
    setSuccesCrearComida,
    permiteInvitados = true,
}) => {
    const asistentesIniciales = useMemo(() => (
        (tarea?.asistentes || []).map(normalizarAsistente)
    ), [tarea]);
    const eventoInicial = useMemo(() => getInitialEventoForm(tarea), [tarea]);
    const usaPreciosTickets = Number(tarea?.id_tipo_evento) === 1;
    const encargadosIniciales = useMemo(() => (
        getOpcionesEncargadosEvento(tarea, opTrabajadores)
    ), [tarea, opTrabajadores]);

    const [asistentes, setAsistentes] = useState(asistentesIniciales);
    const [encargados, setEncargados] = useState(encargadosIniciales);
    const [miembroAsiste, setMiembroAsiste] = useState(true);
    const [trabajadorSelec, setTrabajadorSelec] = useState(null);
    const [invitados, setInvitados] = useState(invitadosVacios);
    const [eventoForm, setEventoForm] = useState(eventoInicial);
    const [tiposTicketsPagan, setTiposTicketsPagan] = useState([]);
    const [conceptosContables, setConceptosContables] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        if (typeof getConceptosContabilidadActivos === "function") {
            getConceptosContabilidadActivos().then((response) => setConceptosContables(response?.data || []));
        }
    }, [open]);

    useEffect(() => {
        setAsistentes(asistentesIniciales);
        setEventoForm(eventoInicial);
        setEncargados(encargadosIniciales);
    }, [asistentesIniciales, eventoInicial, encargadosIniciales]);

    const handleClose = () => {
        if (!guardando) {
            setOpen(false);
        }
    };

    const handleChangeInvitado = (campo, value) => {
        setInvitados((prev) => ({
            ...prev,
            [campo]: Math.max(0, parseInt(value, 10) || 0),
        }));
    };

    const handleEventoChange = (campo, value) => {
        setEventoForm((prev) => ({
            ...prev,
            [campo]: value,
        }));
    };

    useEffect(() => {
        const cargarTiposTickets = async () => {
            if (!open || !usaPreciosTickets) {
                setTiposTicketsPagan([]);
                return;
            }

            const anyo = Number(String(eventoForm.fecha || "").slice(0, 4)) || new Date().getFullYear();
            try {
                const response = await getTiposTickets(1, anyo);
                const tiposPagan = (response?.data || []).filter((tipo) => Number(tipo.paga) === 1);
                setTiposTicketsPagan(tiposPagan);
                setEventoForm((prev) => {
                    const precios = { ...prev.precios_tickets };
                    tiposPagan.forEach((tipo) => {
                        const idTipo = Number(tipo.id);
                        if (precios[idTipo] === undefined) {
                            precios[idTipo] = Number(tarea?.precio_persona || 0) || "";
                        }
                    });

                    return {
                        ...prev,
                        precios_tickets: precios,
                    };
                });
            } catch (err) {
                console.error("Error en carregar tipus de tiquets:", err);
                setTiposTicketsPagan([]);
            }
        };

        cargarTiposTickets();
    }, [open, usaPreciosTickets, eventoForm.fecha, tarea?.precio_persona]);

    const handlePrecioTicketChange = (idTipoTicket, value) => {
        setEventoForm((prev) => ({
            ...prev,
            precios_tickets: {
                ...prev.precios_tickets,
                [Number(idTipoTicket)]: value,
            },
        }));
    };

    const preciosTicketsPayload = tiposTicketsPagan.map((tipo) => ({
        id_tipo_ticket: Number(tipo.id),
        precio: Number(eventoForm.precios_tickets[Number(tipo.id)] || 0),
    }));
    const tienePreciosCargados = usaPreciosTickets && tiposTicketsPagan.length > 0;
    const tiposTicketsTabla = ((tarea?.tipos_tickets?.length ? tarea.tipos_tickets : tiposTicketsPagan) || []).slice(0, 3);
    const etiquetasTicketsTabla = ['Adults', '<= 15', '> 15'].map((predeterminada, indice) => (
        tiposTicketsTabla[indice]?.descripcion_corta || tiposTicketsTabla[indice]?.nombre || predeterminada
    ));

    const handleChangeInvitadoAsistente = (id, campo, value) => {
        setAsistentes((prev) => (
            prev.map((asistente) => (
                asistente.id_usuario === Number(id)
                    ? {
                        ...asistente,
                        tickets: undefined,
                        invitados: {
                            ...asistente.invitados,
                            [campo]: Math.max(0, parseInt(value, 10) || 0),
                        },
                    }
                    : asistente
            ))
        ));
    };

    const handleAgregarAsistente = () => {
        const idUsuario = getOptionId(trabajadorSelec);

        if (!idUsuario) {
            return;
        }

        const nuevoAsistente = {
            id_usuario: idUsuario,
            miembro_asiste: miembroAsiste ? 1 : 0,
            nombre: trabajadorSelec.nombre || trabajadorSelec.Nombre || trabajadorSelec.label || "Sense nom",
            invitados: permiteInvitados ? normalizarInvitados(invitados) : invitadosVacios,
        };

        setAsistentes((prev) => {
            const existe = prev.some((asistente) => asistente.id_usuario === idUsuario);

            if (existe) {
                return prev.map((asistente) => (
                    asistente.id_usuario === idUsuario ? nuevoAsistente : asistente
                ));
            }

            return [...prev, nuevoAsistente];
        });

        setMiembroAsiste(true);
        setTrabajadorSelec(null);
        setInvitados(invitadosVacios);
    };

    const handleEliminarAsistente = (id) => {
        setAsistentes((prev) => prev.filter((asistente) => asistente.id_usuario !== Number(id)));
    };

    const handleGuardar = async () => {
        if (!pagoMiembroValido(eventoForm)) return;
        if (!eventoForm.asunto.trim() || !eventoForm.fecha || !eventoForm.hora_inicio || encargados.length === 0) {
            setError("El nom, la data, l'hora d'inici i els encarregats són obligatoris.");
            return;
        }

        setGuardando(true);
        setError("");

        try {
            const response = await postAsistentes({
                id_evento: tarea.id,
                evento: {
                    ...pagoMiembroPayload(eventoForm),
                    asunto: eventoForm.asunto.trim(),
                    descripcion: eventoForm.descripcion.trim(),
                    fecha: eventoForm.fecha,
                    hora_inicio: eventoForm.hora_inicio,
                    hora_final: eventoForm.hora_final || null,
                    invitados_penya: Math.max(0, Number(eventoForm.invitados_penya || 0)),
                    precio_persona: tienePreciosCargados
                        ? (preciosTicketsPayload.find((item) => item.precio > 0)?.precio || 0)
                        : (tarea.precio_persona || 0),
                    precios_tickets: tienePreciosCargados ? preciosTicketsPayload : undefined,
                    id_usuarios_encargados: encargados.map((encargado) => encargado.value),
                    id_apartado: eventoForm.id_apartado || null,
                },
                asistentes: asistentes.map(normalizarAsistente),
            });

            if (response?.success) {
                setSuccesCrearComida(true);
                setOpen(false);
                return;
            }

            setError(response?.message || "No s'han pogut guardar els assistents.");
        } catch (err) {
            console.error("Error guardant assistents:", err);
            setError("No s'han pogut guardar els assistents.");
        } finally {
            setGuardando(false);
        }
    };

    const opcionesFiltradas = (opTrabajadores || []).filter((trabajador) => (
        !asistentes.some((asistente) => asistente.id_usuario === getOptionId(trabajador))
    ));

    const totales = asistentes.reduce((acc, asistente) => {
        acc.usuarios += Number(asistente.miembro_asiste ?? 1) === 1 ? 1 : 0;
        acc.adultos += getInvitadosValor(asistente.invitados, "invitados");
        acc.nino1 += getInvitadosValor(asistente.invitados, "invitados_nino1");
        acc.nino2 += getInvitadosValor(asistente.invitados, "invitados_nino2");
        return acc;
    }, {
        usuarios: 0,
        adultos: 0,
        nino1: 0,
        nino2: 0,
    });

    const totalGeneral = permiteInvitados ? totales.usuarios + totales.adultos + totales.nino1 + totales.nino2 : totales.usuarios;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: "event-edit-dialog-paper" }}
        >
            <DialogTitle className="event-edit-title">
                <EditIcon />
                <Box className="event-edit-title-copy">
                    <Typography variant="h5">{eventoForm.asunto || tarea.asunto}</Typography>
                    <Typography color="text.secondary">
                        {eventoForm.fecha || tarea.fecha} · {eventoForm.hora_inicio || "--:--"}{eventoForm.hora_final ? ` - ${eventoForm.hora_final}` : ""}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Stack className="event-edit-stack">
                    {error && <Alert severity="error">{error}</Alert>}

                    <Box className="event-edit-details-panel">
                        <Typography variant="h6">Dades de l'esdeveniment</Typography>
                        <Grid container spacing={2} className="event-edit-details-grid">
                            <Grid size={{ xs: 12, md: 12 }}>
                                <TextField
                                    label="Nom"
                                    value={eventoForm.asunto}
                                    onChange={(event) => handleEventoChange("asunto", event.target.value)}
                                    fullWidth
                                    required
                                    inputProps={{ maxLength: 50 }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    label="Descripció"
                                    value={eventoForm.descripcion}
                                    onChange={(event) => handleEventoChange("descripcion", event.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                    {[1, 3].includes(Number(tarea?.id_tipo_evento)) && <PagoMiembroFields form={eventoForm} onChange={handleEventoChange} invitadosPenya={eventoForm.invitados_penya} onInvitadosPenyaChange={(value) => handleEventoChange("invitados_penya", value)} />}
                            <Grid size={{ xs: 12 }}>
                                <TextField select label="Concepte comptable" value={eventoForm.id_apartado} onChange={(event) => handleEventoChange("id_apartado", event.target.value)} fullWidth>
                                    <MenuItem value="">Sense assignar</MenuItem>
                                    {conceptosContables.map((concepte) => <MenuItem key={concepte.id} value={concepte.id}>{concepte.nombre}</MenuItem>)}
                                </TextField>
                            </Grid>
                            {usaPreciosTickets && tiposTicketsPagan.map((tipoTicket) => (
                                <Grid key={tipoTicket.id} size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label={`Preu ${tipoTicket.nombre}`}
                                        type="number"
                                        value={eventoForm.precios_tickets[Number(tipoTicket.id)] || ""}
                                        onChange={(event) => handlePrecioTicketChange(tipoTicket.id, event.target.value)}
                                        fullWidth
                                        inputProps={{ min: 0, step: "0.01" }}
                                    />
                                </Grid>
                            ))}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={opTrabajadores || []}
                                    getOptionLabel={(option) => option.nombre || option.Nombre || option.label || ""}
                                    value={encargados}
                                    onChange={(event, value) => setEncargados(value)}
                                    isOptionEqualToValue={(option, value) => getOptionId(option) === getOptionId(value)}
                                    renderInput={(params) => (
                                        <TextField {...params} required label="Usuaris encarregats" fullWidth />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Data"
                                    type="date"
                                    value={eventoForm.fecha}
                                    onChange={(event) => handleEventoChange("fecha", event.target.value)}
                                    fullWidth
                                    required
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    label="Hora d'inici"
                                    type="time"
                                    value={eventoForm.hora_inicio}
                                    onChange={(event) => handleEventoChange("hora_inicio", event.target.value)}
                                    fullWidth
                                    required
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    label="Hora final"
                                    type="time"
                                    value={eventoForm.hora_final}
                                    onChange={(event) => handleEventoChange("hora_final", event.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box className="event-edit-add-panel">
                        <Typography variant="h6">Afegir assistent</Typography>
                        <Grid container spacing={2} className="event-edit-add-grid">
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel label="Membre Asisteix" control={<Checkbox checked={miembroAsiste} onChange={(event) => setMiembroAsiste(event.target.checked)} />} />
                            </Grid>
                            <Grid size={{ xs: 12, md: 5 }}>
                                <Autocomplete
                                    options={opcionesFiltradas}
                                    getOptionLabel={(option) => option.nombre || option.Nombre || option.label || ""}
                                    value={trabajadorSelec}
                                    onChange={(event, newValue) => setTrabajadorSelec(newValue)}
                                    isOptionEqualToValue={(option, value) => getOptionId(option) === getOptionId(value)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Seleccionar assistent" fullWidth />
                                    )}
                                />
                            </Grid>
                            {permiteInvitados && (
                                <>
                                    <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                                        <TextField
                                            type="number"
                                            label={etiquetasTicketsTabla[0]}
                                            value={invitados.invitados}
                                            onChange={(event) => handleChangeInvitado("invitados", event.target.value)}
                                            fullWidth
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                                        <TextField
                                            type="number"
                                            label={etiquetasTicketsTabla[1]}
                                            value={invitados.invitados_nino1}
                                            onChange={(event) => handleChangeInvitado("invitados_nino1", event.target.value)}
                                            fullWidth
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                                        <TextField
                                            type="number"
                                            label={etiquetasTicketsTabla[2]}
                                            value={invitados.invitados_nino2}
                                            onChange={(event) => handleChangeInvitado("invitados_nino2", event.target.value)}
                                            fullWidth
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid size={{ xs: 12, md: 1 }}>
                                <Button
                                    onClick={handleAgregarAsistente}
                                    variant="contained"
                                    disabled={!trabajadorSelec}
                                    fullWidth
                                    className="event-edit-add-button"
                                >
                                    <PersonAddAlt1Icon />
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" className="event-edit-total">
                            Total assistents: <strong>{totalGeneral}</strong>
                        </Typography>
                        <Box className="event-edit-table-wrap">
                            <Table size="small" className="event-edit-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nom</TableCell>
                                        <TableCell align="center">Membre Asisteix<br /><strong>{totales.usuarios}</strong></TableCell>
                                        {permiteInvitados && (
                                            <>
                                                <TableCell align="center">{etiquetasTicketsTabla[0]}<br /><strong>{totales.adultos}</strong></TableCell>
                                                <TableCell align="center">{etiquetasTicketsTabla[1]}<br /><strong>{totales.nino1}</strong></TableCell>
                                                <TableCell align="center">{etiquetasTicketsTabla[2]}<br /><strong>{totales.nino2}</strong></TableCell>
                                            </>
                                        )}
                                        <TableCell align="center">Acció</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {asistentes.map((asistente) => (
                                        <TableRow key={asistente.id_usuario}>
                                            <TableCell>{asistente.nombre}</TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={Number(asistente.miembro_asiste ?? 1) === 1}
                                                    slotProps={{ input: { 'aria-label': 'Membre Asisteix ' + asistente.nombre } }}
                                                    onChange={(event) => setAsistentes((prev) => prev.map((item) => item.id_usuario === asistente.id_usuario
                                                        ? { ...item, miembro_asiste: event.target.checked ? 1 : 0 } : item))}
                                                />
                                            </TableCell>
                                            {permiteInvitados && (
                                                <>
                                                    <TableCell align="center">
                                                        <TextField
                                                            type="number"
                                                            value={getInvitadosValor(asistente.invitados, "invitados")}
                                                            onChange={(event) => handleChangeInvitadoAsistente(asistente.id_usuario, "invitados", event.target.value)}
                                                            size="small"
                                                            inputProps={{ min: 0 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <TextField
                                                            type="number"
                                                            value={getInvitadosValor(asistente.invitados, "invitados_nino1")}
                                                            onChange={(event) => handleChangeInvitadoAsistente(asistente.id_usuario, "invitados_nino1", event.target.value)}
                                                            size="small"
                                                            inputProps={{ min: 0 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <TextField
                                                            type="number"
                                                            value={getInvitadosValor(asistente.invitados, "invitados_nino2")}
                                                            onChange={(event) => handleChangeInvitadoAsistente(asistente.id_usuario, "invitados_nino2", event.target.value)}
                                                            size="small"
                                                            inputProps={{ min: 0 }}
                                                        />
                                                    </TableCell>
                                                </>
                                            )}
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleEliminarAsistente(asistente.id_usuario)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {asistentes.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={permiteInvitados ? 6 : 3} align="center">No hi ha assistents</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions className="event-edit-actions">
                <Button onClick={handleClose} variant="outlined" disabled={guardando}>
                    Tancar
                </Button>
                <Button onClick={handleGuardar} variant="contained" disabled={guardando}>
                    {guardando ? "Guardant..." : "Guardar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
